import { BrainInterface, InheritanceStatus, LocationVarKey, VarDef, VarKey, isNLPBrain } from "@swivl-mono/types";
import { Button, ButtonVariant, Select, SelectItem, Spinner } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BrainsModel } from "../../../Models/Brains/Brains.Model";
import ModalModel from "../../../Models/Modal/Modal.Model";
import NLPModel from "../../../Models/NLP/NLP.Model";
import LocationVarsModel from "../../../Models/Variables/LocationVars.Model";
import "./BrainsSettingView.scss";
import { AddBrainsModal } from "./Modal/AddBrainsModal";

function nameForLLMOrCLUBrainCache(brain: BrainInterface) {
  if (brain.model) {
    const model = brain.model;
    if (model.model) {
      return model.model;
    }
    if (model.projectName && model.deploymentName) {
      return `${model.projectName} - ${model.deploymentName}`;
    }
  }

  return "";
}

function BrainItem(ItemProps) {
  const { brain, cachesMap } = ItemProps;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const deleteBrain = async () => {
    if (window.confirm("Are you sure you want to delete this brain?")) {
      setLoading(true);
      await BrainsModel.deleteBrain(brain);
      setLoading(false);
    }
  };

  if (isNLPBrain(brain)) {
    return (
      <div className="BrainItem">
        <div className="type mr-1">{brain.type}</div>
        <div className="inheritance">{brain.inheritance === InheritanceStatus.IS_PARENT && "(PARENT)"}</div>
        <div className="name">{brain.name}</div>
        <div className="description mr-2">{NLPModel.nameForCachedModel(brain.model.appId, brain.model.version)}</div>
        <div className="flex gap-1">
          {brain.inheritance !== InheritanceStatus.IS_PARENT && (
            <Button loading={loading} onClick={deleteBrain} variant={ButtonVariant.GHOST}>
              Delete
            </Button>
          )}
          <Button loading={loading} onClick={() => navigate(`/brain/${brain.slug}`)}>
            {brain.inheritance === InheritanceStatus.IS_PARENT ? "View" : "Edit"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="BrainItem">
      <div className="type llm">{brain.type}</div>
      <div className="inheritance">{brain.inheritance === InheritanceStatus.IS_PARENT && "(PARENT)"}</div>
      <div className="name">{brain.name}</div>
      <div className="description mr-2">{nameForLLMOrCLUBrainCache(brain)}</div>

      <div className="flex gap-1">
        {brain.inheritance !== InheritanceStatus.IS_PARENT && (
          <Button onClick={deleteBrain} loading={loading} variant={ButtonVariant.GHOST}>
            Delete
          </Button>
        )}
        <Button onClick={() => navigate(`/brain/${brain.slug}`)} loading={loading}>
          {brain.inheritance === InheritanceStatus.IS_PARENT ? "View" : "Edit"}
        </Button>
      </div>
    </div>
  );
}

export default function BrainsSettingView() {
  const { brains } = BrainsModel.useState((state) => state);
  const { orgVariables } = LocationVarsModel.useState((s) => s);

  const { cachesMap } = NLPModel.useState((state) => state);
  const navigate = useNavigate();
  useEffect(() => {
    BrainsModel.load(false);
    NLPModel.loadCaches();
  }, []);

  if (!brains) {
    return (
      <div className="BrainsSettingView">
        <Spinner />{" "}
      </div>
    );
  }

  const addBrainClicked = () => {
    ModalModel.showModalWithContent(null, <AddBrainsModal navigate={navigate} />, 800);
  };

  const globalBrainSlug =
    orgVariables && orgVariables[LocationVarKey.BrainSlug]
      ? orgVariables[LocationVarKey.BrainSlug]?.value
      : VarDef[VarKey.LocationBrainSlug]?.defaultValue || "";

  const brainItems: SelectItem[] = [];
  if (brains) {
    brains.forEach((brainToAdd) => {
      brainItems.push({
        label: brainToAdd.inheritance === InheritanceStatus.IS_PARENT ? brainToAdd.name + " [PARENT]" : brainToAdd.name,
        value: brainToAdd.slug
      });
    });
  }

  return (
    <div className="BrainsSettingView">
      <Select
        label="Global Brain"
        options={brainItems}
        selectedValues={globalBrainSlug ? [globalBrainSlug] : []}
        hideClear
        onChange={(vals) => {
          BrainsModel.setGlobalBrainSlug(vals[0]);
        }}
      />

      <hr />

      <label>Brains</label>

      {brains.map((brain) => {
        return <BrainItem key={brain.slug} brain={brain} cachesMap={cachesMap} />;
      })}
      <Button variant={ButtonVariant.DASHED} onClick={addBrainClicked}>
        + Brain
      </Button>
    </div>
  );
}
